import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MessageSquareWarning, ChevronDown, ChevronRight, BookOpen } from "lucide-react";
import { toast } from "sonner";

const STATUS = { new: "bg-rose-100 text-rose-800", under_review: "bg-[#FDECD0] text-[#7C3F00]", resolved: "bg-emerald-100 text-emerald-800" };
const STATUS_LABEL = { new: "New", under_review: "Under review", resolved: "Resolved" };

function Row({ fb, onChanged }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(fb.resolution_note || "");
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => {
    setBusy(true);
    try { await api.patch(`/feedback/${fb.id}`, { status, resolution_note: note }); toast.success(`Marked ${STATUS_LABEL[status]}`); onChanged(); }
    catch { toast.error("Could not update"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`feedback-row-${fb.id}`} className="border-t border-slate-100">
      <button onClick={() => setOpen(!open)} className="w-full text-left px-3 py-2.5 flex flex-wrap items-center gap-2 hover:bg-slate-50">
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <span data-testid={`feedback-status-${fb.id}`} className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${STATUS[fb.status]}`}>{STATUS_LABEL[fb.status]}</span>
        <span className="text-xs font-semibold text-[#0A2A5E]">{fb.category_label}</span>
        <span className="text-xs text-slate-600 truncate flex-1 min-w-[10rem]">“{fb.question}”</span>
        <span className="text-[11px] text-slate-400 whitespace-nowrap">{fb.user_email} · {new Date(fb.created_at).toLocaleString()}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 bg-[#F5F9FE] text-xs space-y-3">
          {fb.comment && <div><span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">User comment</span><p className="text-slate-800 mt-0.5">{fb.comment}</p></div>}
          <div><span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Response excerpt</span><p className="text-slate-700 mt-0.5 whitespace-pre-wrap">{fb.reply_excerpt}</p></div>
          <div>
            <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Citations in the response</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {fb.citations?.length ? fb.citations.map((c) => <span key={c.doc_id} className="citation-badge"><BookOpen className="w-3 h-3" /> {c.title}{c.source_type ? ` · ${c.source_type}` : ""}</span>) : <span className="italic text-slate-400">No citations recorded</span>}
            </div>
          </div>
          {fb.high_risk_categories?.length > 0 && <div className="text-rose-700 font-semibold">High-risk categories: {fb.high_risk_categories.join(", ")}</div>}
          <div className="text-slate-500">Response ID <span className="font-mono">{fb.response_id}</span> · User ID <span className="font-mono">{fb.user_id}</span>{fb.org_id && <> · Org <span className="font-mono">{fb.org_id}</span></>}</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input data-testid={`feedback-note-${fb.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Resolution note (what was checked / fixed)" className="h-8 text-xs flex-1 min-w-[12rem]" />
            <Button size="sm" variant="outline" disabled={busy} data-testid={`feedback-review-${fb.id}`} onClick={() => setStatus("under_review")} className="h-8 text-xs">Under review</Button>
            <Button size="sm" disabled={busy} data-testid={`feedback-resolve-${fb.id}`} onClick={() => setStatus("resolved")} className="h-8 text-xs bg-emerald-700 hover:bg-emerald-800 text-white">Resolve</Button>
          </div>
          {fb.reviewed_by_name && <div className="text-slate-400">Last reviewed by {fb.reviewed_by_name} · {new Date(fb.reviewed_at).toLocaleString()}</div>}
        </div>
      )}
    </div>
  );
}

export default function FeedbackQueue() {
  const [data, setData] = useState({ items: [], counts: {}, categories: {} });
  const [status, setStatus] = useState("all");
  const [cat, setCat] = useState("all");
  const load = useCallback(() => {
    api.get(`/feedback?status_filter=${status}&category=${cat}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load feedback"));
  }, [status, cat]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card id="feedback" data-testid="feedback-queue" className="bg-white border-slate-200 mb-6 overflow-hidden">
      <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-200">
        <div className="flex-1 min-w-[12rem]">
          <div className="flex items-center gap-2"><MessageSquareWarning className="w-4 h-4 text-[#C7761A]" /><h3 className="font-display font-bold text-[#0A2A5E]">AI response feedback review queue</h3></div>
          <p className="text-xs text-slate-500 mt-0.5">Reports from your organization only · <span data-testid="feedback-count-new" className="font-semibold text-rose-700">{data.counts.new || 0} new</span> · {data.counts.under_review || 0} under review · {data.counts.resolved || 0} resolved</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="feedback-status-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="new">New</SelectItem><SelectItem value="under_review">Under review</SelectItem><SelectItem value="resolved">Resolved</SelectItem></SelectContent>
        </Select>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="feedback-category-filter" className="w-56 h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{Object.entries(data.categories).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {data.items.length === 0 ? <div data-testid="feedback-empty" className="px-4 py-8 text-center text-sm text-slate-500">No feedback reports match these filters.</div> : data.items.map((fb) => <Row key={fb.id} fb={fb} onChanged={load} />)}
    </Card>
  );
}
